import type { PhotoId } from '../gallery/photos';
import type { TextLayer } from '../template';
import {
  emph,
  lin,
  solid,
  step,
  t,
  type Box,
  type RelKey,
  type SceneRef,
  type Tx,
  type VideoBuilder,
} from './kit';
import { SN, type VideoTemplateDef } from './types';

/**
 * Explainer-style videos: a bold hook with a highlighted word, cards that fly in and float,
 * elements popping one by one, bars that fill up and a pulsing button at the end.
 */

const HEAD: Partial<TextLayer> = {
  fontFamily: 'Montserrat',
  fontWeight: 900,
  align: 'left',
  lineHeight: 1.02,
  letterSpacing: 0,
};
const BODY: Partial<TextLayer> = { fontFamily: 'Inter', fontWeight: 500, align: 'left' };

/** A card that flies in from `from` (offset) with a turn, then floats until the scene ends. */
function flyIn(b: VideoBuilder, delay: number, from: { x?: number; y?: number }, turn = 10) {
  const keys: RelKey[] = [
    { at: 0, x: from.x ?? 0, y: from.y ?? 0, rotate: turn, opacity: 0 },
    { at: delay, x: from.x ?? 0, y: from.y ?? 0, rotate: turn, opacity: 0, easing: 'out-expo' },
    { at: delay + 900, x: 0, y: 0, rotate: 0, opacity: 1, easing: 'ease-in-out' },
    { at: -1, x: 0, y: -b.s(0.012), rotate: 0, opacity: 1 },
  ];
  return keys;
}

/** Two-line title: the first line in `color`, the second in `accent`, rising word by word. */
function title(
  b: VideoBuilder,
  sc: SceneRef,
  name: string,
  first: Tx,
  second: Tx,
  box: Box,
  color: string,
  accent: string,
  size = 0.1,
) {
  const h = box.h / 2;
  b.text(
    sc,
    `${name}_1`,
    first,
    { ...box, h },
    { ...HEAD, fontSize: b.s(size), color, maxLines: 1 },
    { enter: step('words-rise', 150, 550), split: { by: 'word', stagger: 90 } },
  );
  b.text(
    sc,
    `${name}_2`,
    second,
    { ...box, y: box.y + h, h },
    { ...HEAD, fontSize: b.s(size), color: accent, maxLines: 1 },
    { enter: step('words-rise', 400, 550), split: { by: 'word', stagger: 90 } },
  );
}

/** A white row with a round marker and a label, sliding in from `direction`. */
function row(
  b: VideoBuilder,
  sc: SceneRef,
  name: string,
  label: Tx,
  box: Box,
  delay: number,
  marker: { icon?: string; color: string; bg: string },
  direction: 'left' | 'right' = 'left',
) {
  // Key frames, not the slide preset: its distance depends on each layer's size, so the card,
  // marker and label would drift apart
  const dx = direction === 'left' ? -b.s(0.3) : b.s(0.3);
  const m = {
    keys: [
      { at: 0, x: dx, opacity: 0 },
      { at: delay, x: dx, opacity: 0, easing: 'out-quart' },
      { at: delay + 600, x: 0, opacity: 1 },
    ],
  };
  b.shape(
    sc,
    `${name}_card`,
    box,
    {
      fill: solid('#FFFFFF'),
      borderRadius: Math.round(box.h * 0.28),
      shadow: { x: 0, y: b.s(0.012), blur: b.s(0.04), color: '#1E1B4B33' },
    },
    m,
  );
  const d = box.h * 0.56;
  const dot = { x: box.x + box.h * 0.24, y: box.y + (box.h - d) / 2, w: d, h: d };
  b.shape(sc, `${name}_dot`, dot, { shape: 'ellipse', fill: solid(marker.bg) }, m);
  if (marker.icon)
    b.icon(
      sc,
      `${name}_icon`,
      marker.icon,
      { x: dot.x + d * 0.22, y: dot.y + d * 0.22, w: d * 0.56, h: d * 0.56 },
      marker.color,
      m,
    );
  b.text(
    sc,
    name,
    label,
    { x: dot.x + d + box.h * 0.24, y: box.y, w: box.w - d - box.h * 0.72, h: box.h },
    {
      fontFamily: 'Montserrat',
      fontWeight: 800,
      fontSize: Math.round(box.h * 0.36),
      color: '#18181B',
      align: 'left',
      maxLines: 1,
    },
    m,
  );
}

/** A button that pops in and then keeps pulsing. */
function button(
  b: VideoBuilder,
  sc: SceneRef,
  label: Tx,
  at: { x: number; y: number; h: number },
  bg: string,
  color: string,
  delay: number,
) {
  b.chip(
    sc,
    'cta_button',
    label,
    at,
    {
      bg,
      color,
      font: 'Montserrat',
      weight: 900,
      size: Math.round(at.h * 0.4),
      extra: { shadow: { x: 0, y: b.s(0.015), blur: b.s(0.05), color: '#00000040' } },
    },
    {
      enter: step('pop', delay, 550, 'spring-bouncy'),
      emphasis: emph('pulse', delay + 700, 900, 0),
    },
  );
}

const C = {
  night1: '#1E1B4B',
  night2: '#4338CA',
  light1: '#EEF2FF',
  light2: '#FFFFFF',
  violet: '#4F46E5',
  yellow: '#FACC15',
  amber: '#F59E0B',
  ink: '#18181B',
  soft: '#C7D2FE',
  grey: '#52525B',
  track: '#E0E7FF',
  pink: '#E11D48',
  teal: '#0D9488',
  orange: '#EA580C',
  mint: '#DCFCE7',
  green: '#16A34A',
};

const FEATURE_LAUNCH: VideoTemplateDef = {
  id: 'feature-launch',
  category: 'tech',
  format: 'vertical',
  sound: true,
  name: t(
    'Feature launch',
    'Запуск функції',
    'Запуск функции',
    'Premiera funkcji',
    'Lansare de funcție',
    'Lanzamiento de función',
  ),
  description: t(
    'Words rise, a card flies in, steps pop one by one, bars fill up, the button pulses',
    'Слова виринають, картка влітає, кроки з’являються по черзі, шкали заповнюються',
    'Слова всплывают, карточка влетает, шаги появляются по очереди, шкалы заполняются',
    'Słowa wyskakują, karta wlatuje, kroki pojawiają się po kolei, paski się wypełniają',
    'Cuvintele urcă, cardul intră, pașii apar pe rând, barele se umplu',
    'Las palabras suben, la tarjeta entra, los pasos aparecen uno a uno, las barras se llenan',
  ),
  scenes: [
    {
      key: 'hook',
      name: SN.hook,
      duration: 3000,
      // Not a clip transition: the next scene reveals its timeline with clips of its own
      transition: { preset: 'slide-over', duration: 600, params: { direction: 'left' } },
    },
    {
      key: 'steps',
      name: t('Steps', 'Кроки', 'Шаги', 'Kroki', 'Pași', 'Pasos'),
      duration: 3400,
      transition: { preset: 'zoom-in', duration: 500 },
    },
    {
      key: 'results',
      name: t('Results', 'Результати', 'Результаты', 'Wyniki', 'Rezultate', 'Resultados'),
      duration: 3400,
      transition: { preset: 'push', duration: 550, params: { direction: 'up' } },
    },
    { key: 'cta', name: SN.cta, duration: 3200 },
  ],
  build(b) {
    const F = b.F;
    const up = b.upright;

    // ---- Hook: headline with a wiggling accent word, a card flying in, a progress bar
    const s0 = b.sc('hook');
    const copy = b.pick({
      upright: b.in(0, 0, 1, 0.5),
      square: b.in(0, 0.06, 0.56, 0.88),
      wide: b.in(0, 0.08, 0.54, 0.84),
    });
    const ch = b.s(0.06);
    b.chip(
      s0,
      'kicker',
      t('NEW FEATURE', 'НОВА ФУНКЦІЯ', 'НОВАЯ ФУНКЦИЯ', 'NOWOŚĆ', 'FUNCȚIE NOUĂ', 'NOVEDAD'),
      { x: copy.x, y: copy.y, h: ch },
      { bg: '#FFFFFF26', color: '#E0E7FF', upper: true, weight: 800, size: Math.round(ch * 0.38) },
      { enter: step('fade', 0, 400) },
    );
    b.text(
      s0,
      'headline',
      t('Your workflow', 'Ваша робота', 'Ваша работа', 'Twoja praca', 'Munca ta', 'Tu trabajo'),
      { x: copy.x, y: copy.y + copy.h * 0.17, w: copy.w, h: copy.h * 0.32 },
      { ...HEAD, fontSize: b.s(up ? 0.13 : 0.11), color: '#FFFFFF' },
      { enter: step('words-rise', 250, 600), split: { by: 'word', stagger: 110 } },
    );
    b.text(
      s0,
      'accent',
      t(
        'now flies.',
        'тепер летить.',
        'теперь летит.',
        'teraz leci.',
        'acum zboară.',
        'ahora vuela.',
      ),
      { x: copy.x, y: copy.y + copy.h * 0.5, w: copy.w, h: copy.h * 0.2 },
      { ...HEAD, fontSize: b.s(up ? 0.13 : 0.11), color: C.yellow, maxLines: 1 },
      {
        enter: step('pop', 800, 550, 'spring-bouncy'),
        emphasis: emph('wiggle', 1400, 500, 2, 250),
      },
    );
    b.text(
      s0,
      'subline',
      t(
        'The same tools, twice as fast.',
        'Ті самі інструменти — удвічі швидше.',
        'Те же инструменты — вдвое быстрее.',
        'Te same narzędzia, dwa razy szybciej.',
        'Aceleași unelte, de două ori mai rapid.',
        'Las mismas herramientas, el doble de rápido.',
      ),
      { x: copy.x, y: copy.y + copy.h * 0.73, w: up ? copy.w * 0.55 : copy.w, h: copy.h * 0.2 },
      { ...BODY, fontSize: b.s(0.038), color: C.soft, lineHeight: 1.3, verticalAlign: 'top' },
      { enter: step('rise', 1200, 500) },
    );

    const cardH = b.pick({ upright: F.h * 0.42, square: F.h * 0.8, wide: F.h * 0.86 });
    const cardW = cardH * 0.5625;
    const card: Box = b.pick({
      upright: { x: F.x + F.w - cardW - F.w * 0.04, y: F.y + F.h * 0.55, w: cardW, h: cardH },
      other: { x: F.x + F.w - cardW - F.w * 0.06, y: F.y + (F.h - cardH) / 2, w: cardW, h: cardH },
    });
    b.shape(
      s0,
      'card_ghost',
      { ...card, x: card.x - cardW * 0.14, y: card.y + cardW * 0.08 },
      { fill: solid(C.amber), borderRadius: cardW * 0.1, opacity: 0.35, rotation: -4 },
      { enter: step('fade', 1000, 500) },
    );
    const keys = flyIn(b, 500, { x: Math.round(b.W * 0.5) }, 14);
    const cm = { keys, emphasis: emph('float', 1500, 3000, 0) };
    b.shape(
      s0,
      'card',
      card,
      {
        fill: lin(160, C.yellow, C.amber),
        borderRadius: cardW * 0.1,
        rotation: 5,
        shadow: { x: 0, y: b.s(0.03), blur: b.s(0.08), color: '#00000080' },
      },
      cm,
    );
    // A screen drawn from shapes: header line, two text lines and a play button
    const pad = cardW * 0.12;
    [0.5, 0.75, 0.38].forEach((w, i) =>
      b.shape(
        s0,
        `card_line_${i + 1}`,
        {
          x: card.x + pad,
          y: card.y + pad + i * cardW * 0.13,
          w: (cardW - pad * 2) * w,
          h: cardW * (i === 0 ? 0.08 : 0.05),
        },
        { fill: solid(i === 0 ? C.ink : '#18181B55'), borderRadius: cardW, rotation: 5 },
        cm,
      ),
    );
    const pd = cardW * 0.36;
    const play = b.centered(card.x + cardW / 2, card.y + cardH * 0.6, pd, pd);
    const pm = { keys, emphasis: emph('pulse', 1700, 900, 0) };
    b.shape(
      s0,
      'card_play',
      play,
      {
        shape: 'ellipse',
        fill: solid('#FFFFFF'),
        shadow: { x: 0, y: b.s(0.01), blur: b.s(0.03), color: '#00000040' },
      },
      pm,
    );
    b.icon(
      s0,
      'card_play_icon',
      'play',
      b.centered(play.x + pd * 0.54, play.y + pd / 2, pd * 0.46, pd * 0.46),
      C.violet,
      pm,
    );

    const bar: Box = up
      ? { x: F.x, y: card.y + cardH - b.s(0.02), w: card.x - F.x - b.s(0.08), h: b.s(0.014) }
      : { x: copy.x, y: copy.y + copy.h * 0.97, w: copy.w * 0.8, h: b.s(0.012) };
    b.shape(s0, 'progress_track', bar, { fill: solid('#FFFFFF2E'), borderRadius: bar.h });
    b.shape(
      s0,
      'progress_fill',
      bar,
      { fill: solid(C.yellow), borderRadius: bar.h },
      { enter: step('wipe', 1100, 1700, 'linear') },
    );

    // ---- Steps: four cards pop one after another, the timeline fills under them
    const s1 = b.sc('steps');
    b.backdrop(s1, 'steps_bg', lin(180, C.light1, C.light2));
    title(
      b,
      s1,
      'steps_title',
      t(
        'One app.',
        'Один застосунок.',
        'Одно приложение.',
        'Jedna aplikacja.',
        'O aplicație.',
        'Una app.',
      ),
      t(
        'Four steps.',
        'Чотири кроки.',
        'Четыре шага.',
        'Cztery kroki.',
        'Patru pași.',
        'Cuatro pasos.',
      ),
      b.in(0, 0, 1, up ? 0.22 : 0.24),
      C.ink,
      C.violet,
      0.09,
    );
    const area = b.pick({
      upright: b.in(0, 0.3, 1, 0.46),
      square: b.in(0, 0.32, 1, 0.38),
      wide: b.in(0, 0.32, 1, 0.42),
    });
    // A row with arrows between the cards; a 2×2 grid on upright videos
    const cols = up ? 2 : 4;
    const sw = up ? (area.w - b.s(0.03)) / 2 : area.w / (4 + 3 * 0.35);
    const gap = up ? b.s(0.03) : sw * 0.35;
    const sh = up ? (area.h - gap) / 2 : Math.min(area.h, sw * 1.6);
    const sy0 = area.y + (up ? 0 : (area.h - sh) / 2);
    const stepColors = [C.night2, C.pink, C.teal, C.orange];
    const labels = [
      t('Plan', 'План', 'План', 'Plan', 'Plan', 'Planea'),
      t('Create', 'Створи', 'Создай', 'Twórz', 'Creează', 'Crea'),
      t('Share', 'Поділись', 'Поделись', 'Udostępnij', 'Distribuie', 'Comparte'),
      t('Grow', 'Рости', 'Расти', 'Rośnij', 'Crește', 'Crece'),
    ];
    labels.forEach((label, i) => {
      const x = area.x + (i % cols) * (sw + gap);
      const sy = sy0 + Math.floor(i / cols) * (sh + gap);
      const m = { enter: step('pop', 600 + i * 300, 500, 'spring-bouncy') };
      b.shape(
        s1,
        `step_card_${i + 1}`,
        { x, y: sy, w: sw, h: sh },
        {
          fill: solid(stepColors[i]),
          borderRadius: sw * 0.14,
          shadow: { x: 0, y: b.s(0.012), blur: b.s(0.04), color: '#1E1B4B40' },
        },
        m,
      );
      b.text(
        s1,
        `step_number_${i + 1}`,
        String(i + 1),
        { x: x + sw * 0.1, y: sy + sh * 0.08, w: sw * 0.8, h: sh * 0.5 },
        {
          ...HEAD,
          fontSize: Math.round(Math.min(sw * 0.55, sh * 0.42)),
          color: '#FFFFFF',
          align: 'left',
        },
        m,
      );
      b.text(
        s1,
        `step_${i + 1}`,
        label,
        { x: x + sw * 0.1, y: sy + sh * 0.7, w: sw * 0.8, h: sh * 0.22 },
        {
          fontFamily: 'Montserrat',
          fontWeight: 800,
          fontSize: Math.round(Math.min(sw * 0.15, sh * 0.13)),
          color: '#FFFFFF',
          align: 'left',
          maxLines: 1,
        },
        m,
      );
      if (!up && i < 3) {
        const d = gap * 0.8;
        const c = b.centered(x + sw + gap / 2, sy + sh / 2, d, d);
        const am = { enter: step('pop', 750 + i * 300, 400, 'spring-bouncy') };
        b.shape(s1, `step_arrow_bg_${i + 1}`, c, { shape: 'ellipse', fill: solid(C.violet) }, am);
        b.icon(
          s1,
          `step_arrow_${i + 1}`,
          'arrow-right',
          b.centered(c.x + d / 2, c.y + d / 2, d * 0.55, d * 0.55),
          '#FFFFFF',
          am,
        );
      }
    });
    const tlY = area.y + area.h + F.h * 0.06;
    const tlH = b.s(0.05);
    const parts = [3, 4, 3, 3];
    const total = parts.reduce((a, v) => a + v, 0);
    let px = F.x;
    parts.forEach((p, i) => {
      const w = (F.w * p) / total;
      b.shape(
        s1,
        `timeline_${i + 1}`,
        { x: px, y: tlY, w, h: tlH },
        { fill: solid(stepColors[i]) },
        { enter: step('wipe', 1900 + i * 260, 300, 'linear') },
      );
      px += w;
    });
    b.shape(
      s1,
      'playhead',
      { x: F.x, y: tlY - tlH * 0.3, w: Math.max(3, b.s(0.004)), h: tlH * 1.6 },
      { fill: solid('#EF4444') },
      {
        enter: step('fade', 1900, 200),
        keys: [
          { at: 1900, x: 0, easing: 'linear' },
          { at: 3150, x: Math.round(F.w - b.s(0.004)) },
        ],
      },
    );

    // ---- Results: bars fill up one by one, values pop at their ends
    const s2 = b.sc('results');
    b.backdrop(s2, 'results_bg', lin(180, C.light1, C.light2));
    title(
      b,
      s2,
      'results_title',
      t('Results', 'Результати,', 'Результаты,', 'Wyniki,', 'Rezultate', 'Resultados'),
      t('that add up', 'що помітні', 'которые видно', 'które widać', 'care se văd', 'que se notan'),
      b.in(0, 0, 1, up ? 0.22 : 0.24),
      C.ink,
      C.violet,
      0.09,
    );
    const stats: Array<{ label: Tx; value: string; share: number; color: string }> = [
      {
        label: t(
          'Faster launches',
          'Швидші запуски',
          'Быстрее запуски',
          'Szybsze premiery',
          'Lansări mai rapide',
          'Lanzamientos más rápidos',
        ),
        value: '+42%',
        share: 0.82,
        color: C.violet,
      },
      {
        label: t(
          'More done',
          'Більше зроблено',
          'Больше сделано',
          'Więcej zrobione',
          'Mai mult făcut',
          'Más hecho',
        ),
        value: '3×',
        share: 0.95,
        color: C.pink,
      },
      {
        label: t(
          'Less busywork',
          'Менше рутини',
          'Меньше рутины',
          'Mniej rutyny',
          'Mai puțină rutină',
          'Menos rutina',
        ),
        value: '−60%',
        share: 0.6,
        color: C.teal,
      },
      {
        label: t(
          'Always on',
          'Завжди онлайн',
          'Всегда онлайн',
          'Zawsze online',
          'Mereu online',
          'Siempre activo',
        ),
        value: '24/7',
        share: 1,
        color: C.orange,
      },
    ];
    const ry = F.y + F.h * (up ? 0.3 : 0.32);
    const rh = (F.y + F.h * 0.97 - ry) / stats.length;
    stats.forEach((s, i) => {
      const y = ry + i * rh;
      const delay = 700 + i * 350;
      b.text(
        s2,
        `stat_label_${i + 1}`,
        s.label,
        { x: F.x, y, w: F.w * 0.72, h: rh * 0.4 },
        { ...BODY, fontWeight: 700, fontSize: b.s(0.04), color: C.ink, maxLines: 1 },
        { enter: step('fade', delay - 200, 400) },
      );
      const track: Box = { x: F.x, y: y + rh * 0.46, w: F.w, h: rh * 0.2 };
      b.shape(s2, `stat_track_${i + 1}`, track, { fill: solid(C.track), borderRadius: track.h });
      b.shape(
        s2,
        `stat_bar_${i + 1}`,
        { ...track, w: track.w * s.share },
        { fill: solid(s.color), borderRadius: track.h },
        { enter: step('wipe', delay, 700, 'out-quart') },
      );
      b.text(
        s2,
        `stat_value_${i + 1}`,
        s.value,
        { x: F.x + F.w * 0.72, y, w: F.w * 0.28, h: rh * 0.4 },
        { ...HEAD, fontSize: b.s(0.05), color: s.color, align: 'right', maxLines: 1 },
        { enter: step('pop', delay + 600, 450, 'spring-bouncy') },
      );
    });

    // ---- Call to action: checklist slides in, an app icon floats, the button pulses
    const s3 = b.sc('cta');
    const cw = up ? F.w : F.w * 0.6;
    b.text(
      s3,
      'cta_title',
      t(
        'Try it free',
        'Спробуйте безкоштовно',
        'Попробуйте бесплатно',
        'Wypróbuj za darmo',
        'Încearcă gratuit',
        'Pruébalo gratis',
      ),
      { x: F.x, y: F.y, w: cw, h: F.h * 0.22 },
      { ...HEAD, fontSize: b.s(0.12), color: '#FFFFFF', verticalAlign: 'top' },
      { enter: step('words-rise', 150, 600), split: { by: 'word', stagger: 110 } },
    );
    b.text(
      s3,
      'cta_subline',
      t(
        'No card needed. Cancel anytime.',
        'Без картки. Скасування будь-коли.',
        'Без карты. Отмена в любой момент.',
        'Bez karty. Rezygnujesz kiedy chcesz.',
        'Fără card. Anulezi oricând.',
        'Sin tarjeta. Cancela cuando quieras.',
      ),
      { x: F.x, y: F.y + F.h * 0.23, w: cw, h: F.h * 0.08 },
      { ...BODY, fontSize: b.s(0.038), color: C.soft, maxLines: 2 },
      { enter: step('fade', 700, 500) },
    );
    const rowH = b.s(up ? 0.1 : 0.085);
    const items = [
      t(
        'Set up in minutes',
        'Налаштування за хвилини',
        'Настройка за минуты',
        'Gotowe w kilka minut',
        'Gata în câteva minute',
        'Listo en minutos',
      ),
      t(
        'Works with your tools',
        'Працює з вашими сервісами',
        'Работает с вашими сервисами',
        'Działa z Twoimi narzędziami',
        'Merge cu uneltele tale',
        'Funciona con tus herramientas',
      ),
      t(
        'Help when you need it',
        'Допомога, коли потрібно',
        'Помощь, когда нужно',
        'Pomoc, kiedy trzeba',
        'Ajutor când ai nevoie',
        'Ayuda cuando la necesitas',
      ),
    ];
    items.forEach((label, i) =>
      row(
        b,
        s3,
        `check_${i + 1}`,
        label,
        { x: F.x, y: F.y + F.h * 0.36 + i * rowH * 1.25, w: up ? F.w * 0.9 : cw, h: rowH },
        900 + i * 250,
        { icon: 'check', color: C.green, bg: C.mint },
      ),
    );
    const ic = b.s(up ? 0.22 : 0.26);
    const iconBox: Box = up
      ? { x: F.x + F.w - ic, y: F.y + F.h * 0.84 - ic * 0.4, w: ic, h: ic }
      : { x: F.x + F.w - ic - F.w * 0.06, y: F.y + (F.h - ic) / 2, w: ic, h: ic };
    const im = {
      enter: step('pop', 1300, 600, 'spring-bouncy'),
      emphasis: emph('float', 1900, 3000, 0),
    };
    b.shape(
      s3,
      'app_icon',
      iconBox,
      {
        fill: lin(135, C.yellow, C.orange),
        borderRadius: ic * 0.26,
        shadow: { x: 0, y: b.s(0.02), blur: b.s(0.06), color: '#00000066' },
      },
      im,
    );
    b.icon(
      s3,
      'app_icon_glyph',
      'rocket',
      b.centered(iconBox.x + ic / 2, iconBox.y + ic / 2, ic * 0.5, ic * 0.5),
      C.night1,
      im,
    );
    button(
      b,
      s3,
      t('Get started', 'Почати', 'Начать', 'Zacznij', 'Începe', 'Empezar'),
      { x: F.x, y: F.y + F.h * 0.84, h: b.s(0.1) },
      C.yellow,
      C.night1,
      1800,
    );

    b.poster = 'hook';
    return lin(160, C.night1, C.night2);
  },
};

const COLLECTION_SHOWCASE: VideoTemplateDef = {
  id: 'collection-showcase',
  category: 'fashion',
  format: 'vertical',
  sound: true,
  name: t(
    'Collection showcase',
    'Вітрина колекції',
    'Витрина коллекции',
    'Prezentacja kolekcji',
    'Vitrina colecției',
    'Escaparate de colección',
  ),
  description: t(
    'Photo cards fly up, a grid of looks pops in, the offer hits on the beat',
    'Фото-картки злітають, сітка образів з’являється, пропозиція — в такт',
    'Фото-карточки взлетают, сетка образов появляется, предложение — в такт',
    'Karty ze zdjęciami wlatują, siatka stylizacji, oferta w rytm muzyki',
    'Cardurile foto urcă, grila de ținute apare, oferta cade pe ritm',
    'Las fotos suben, la cuadrícula de looks aparece, la oferta cae al ritmo',
  ),
  scenes: [
    { key: 'hook', name: SN.hook, duration: 3200, transition: { preset: 'wipe', duration: 600 } },
    {
      key: 'looks',
      name: t('Looks', 'Образи', 'Образы', 'Stylizacje', 'Ținute', 'Looks'),
      duration: 3400,
      transition: { preset: 'zoom-in', duration: 500 },
    },
    {
      key: 'offer',
      name: SN.offer,
      duration: 3000,
      transition: { preset: 'push', duration: 550, params: { direction: 'up' } },
    },
    { key: 'cta', name: SN.cta, duration: 3200 },
  ],
  build(b) {
    const F = b.F;
    const up = b.upright;
    const P = {
      bg1: '#1C1917',
      bg2: '#7C2D12',
      cream: '#FFF7ED',
      peach: '#FFEDD5',
      accent: '#FB923C',
      gold: '#FACC15',
      ink: '#1C1917',
      soft: '#FED7AA',
    };
    const photoCard = (
      sc: SceneRef,
      name: string,
      photo: PhotoId,
      box: Box,
      rotation: number,
      m: Parameters<VideoBuilder['photo']>[5],
    ) =>
      b.photo(
        sc,
        name,
        photo,
        box,
        {
          borderRadius: Math.round(box.w * 0.08),
          rotation,
          shadow: { x: 0, y: b.s(0.02), blur: b.s(0.06), color: '#00000080' },
        },
        m,
      );

    // ---- Hook: headline, accent pops, three photos fly up and float
    const s0 = b.sc('hook');
    const copy = b.pick({
      upright: b.in(0, 0, 1, 0.46),
      square: b.in(0, 0.06, 0.5, 0.88),
      wide: b.in(0, 0.08, 0.46, 0.84),
    });
    const ch = b.s(0.06);
    b.chip(
      s0,
      'kicker',
      t(
        'NEW COLLECTION',
        'НОВА КОЛЕКЦІЯ',
        'НОВАЯ КОЛЛЕКЦИЯ',
        'NOWA KOLEKCJA',
        'COLECȚIE NOUĂ',
        'NUEVA COLECCIÓN',
      ),
      { x: copy.x, y: copy.y, h: ch },
      { bg: '#FFFFFF26', color: P.soft, upper: true, weight: 800, size: Math.round(ch * 0.38) },
      { enter: step('fade', 0, 400) },
    );
    b.text(
      s0,
      'headline',
      t(
        'New season.',
        'Новий сезон.',
        'Новый сезон.',
        'Nowy sezon.',
        'Sezon nou.',
        'Nueva temporada.',
      ),
      { x: copy.x, y: copy.y + copy.h * 0.18, w: copy.w, h: copy.h * 0.32 },
      { ...HEAD, fontSize: b.s(up ? 0.13 : 0.1), color: '#FFFFFF' },
      { enter: step('words-rise', 250, 600), split: { by: 'word', stagger: 110 } },
    );
    b.text(
      s0,
      'accent',
      t('Your style.', 'Твій стиль.', 'Твой стиль.', 'Twój styl.', 'Stilul tău.', 'Tu estilo.'),
      { x: copy.x, y: copy.y + copy.h * 0.52, w: copy.w, h: copy.h * 0.2 },
      { ...HEAD, fontSize: b.s(up ? 0.13 : 0.1), color: P.gold, maxLines: 1 },
      {
        enter: step('pop', 800, 550, 'spring-bouncy'),
        emphasis: emph('wiggle', 1400, 500, 2, 250),
      },
    );
    b.text(
      s0,
      'subline',
      t(
        'Twelve new pieces, in stores and online.',
        'Дванадцять нових речей у магазинах і онлайн.',
        'Двенадцать новых вещей в магазинах и онлайн.',
        'Dwanaście nowości w sklepach i online.',
        'Douăsprezece piese noi, în magazine și online.',
        'Doce prendas nuevas, en tienda y online.',
      ),
      { x: copy.x, y: copy.y + copy.h * 0.76, w: copy.w, h: copy.h * 0.2 },
      { ...BODY, fontSize: b.s(0.036), color: P.soft, lineHeight: 1.3, verticalAlign: 'top' },
      { enter: step('rise', 1200, 500) },
    );
    const strip = b.pick({
      upright: b.in(0, 0.5, 1, 0.5),
      square: b.in(0.52, 0.04, 0.48, 0.92),
      wide: b.in(0.5, 0.02, 0.5, 0.96),
    });
    // Side by side on upright videos; a fan of overlapping cards beside the copy elsewhere
    const cw = strip.w * (up ? 0.31 : 0.46);
    const chh = Math.min(strip.h * (up ? 0.8 : 0.78), cw * (up ? 1.6 : 1.4));
    const hookPhotos: PhotoId[] = ['fashion-model', 'leather-shoes', 'retro-style'];
    // The middle card goes last so it lies on top of the fan
    [0, 2, 1].forEach((i) => {
      const x = up
        ? strip.x + (strip.w - cw * 3) / 4 + i * (cw + (strip.w - cw * 3) / 4)
        : strip.x + i * ((strip.w - cw) / 2);
      const y = strip.y + (strip.h - chh) / 2 + (i === 1 ? -chh * 0.06 : chh * 0.04);
      photoCard(s0, `look_${i + 1}`, hookPhotos[i], { x, y, w: cw, h: chh }, [-6, 0, 6][i], {
        keys: flyIn(b, 400 + i * 220, { y: Math.round(b.H * 0.6) }, [-12, 0, 12][i]),
        emphasis: emph('float', 1600 + i * 120, 3000, 0),
      });
    });

    // ---- Looks: a grid of six photos pops in one by one
    const s1 = b.sc('looks');
    b.backdrop(s1, 'looks_bg', lin(180, P.cream, '#FFFFFF'));
    title(
      b,
      s1,
      'looks_title',
      t(
        'Six looks.',
        'Шість образів.',
        'Шесть образов.',
        'Sześć stylizacji.',
        'Șase ținute.',
        'Seis looks.',
      ),
      t(
        'One wardrobe.',
        'Один гардероб.',
        'Один гардероб.',
        'Jedna szafa.',
        'O garderobă.',
        'Un armario.',
      ),
      b.in(0, 0, 1, up ? 0.2 : 0.24),
      P.ink,
      P.accent,
      0.09,
    );
    const cols = b.wide ? 6 : 3;
    const rows = b.wide ? 1 : 2;
    const grid = b.pick({
      upright: b.in(0, 0.27, 1, 0.66),
      square: b.in(0.08, 0.3, 0.84, 0.68),
      wide: b.in(0, 0.36, 1, 0.56),
    });
    const g = b.s(0.02);
    const tw = Math.min(
      (grid.w - g * (cols - 1)) / cols,
      ((grid.h - g * (rows - 1)) / rows) * 0.75,
    );
    const th = tw / 0.75;
    const gx = grid.x + (grid.w - (tw * cols + g * (cols - 1))) / 2;
    const gy = grid.y + (grid.h - (th * rows + g * (rows - 1))) / 2;
    const gridPhotos: PhotoId[] = [
      'fashion-model',
      'retro-style',
      'leather-shoes',
      'fashion-selfie',
      'glam-portrait',
      'beauty-portrait',
    ];
    gridPhotos.forEach((ph, i) => {
      const c = i % cols;
      const r = Math.floor(i / cols);
      b.photo(
        s1,
        `grid_${i + 1}`,
        ph,
        { x: gx + c * (tw + g), y: gy + r * (th + g), w: tw, h: th },
        { borderRadius: Math.round(tw * 0.08) },
        { enter: step('pop', 700 + i * 200, 500, 'spring-bouncy') },
      );
    });

    // ---- Offer: a big discount hits, chips slide in, an equalizer keeps the beat
    const s2 = b.sc('offer');
    b.text(
      s2,
      'offer_kicker',
      t(
        'THIS WEEK ONLY',
        'ЛИШЕ ЦЬОГО ТИЖНЯ',
        'ТОЛЬКО НА ЭТОЙ НЕДЕЛЕ',
        'TYLKO W TYM TYGODNIU',
        'DOAR SĂPTĂMÂNA ASTA',
        'SOLO ESTA SEMANA',
      ),
      b.in(0, up ? 0.06 : 0.04, 1, 0.08),
      {
        ...BODY,
        fontWeight: 800,
        fontSize: b.s(0.04),
        color: P.soft,
        letterSpacing: b.s(0.006),
        maxLines: 1,
        align: 'center',
      },
      { enter: step('fade', 100, 400) },
    );
    b.text(
      s2,
      'offer_value',
      '−30%',
      b.in(0, up ? 0.16 : 0.12, 1, up ? 0.3 : 0.38),
      { ...HEAD, fontSize: b.s(0.3), color: P.gold, align: 'center', maxLines: 1 },
      {
        enter: step('pop', 400, 600, 'spring-bouncy'),
        emphasis: emph('wiggle', 1100, 500, 2, 300),
      },
    );
    const chips = [
      t(
        'Free shipping',
        'Безкоштовна доставка',
        'Бесплатная доставка',
        'Darmowa dostawa',
        'Livrare gratuită',
        'Envío gratis',
      ),
      t(
        'Easy returns',
        'Легке повернення',
        'Простой возврат',
        'Łatwe zwroty',
        'Retur ușor',
        'Devolución fácil',
      ),
    ];
    const chipH = b.s(0.08);
    chips.forEach((label, i) =>
      b.chip(
        s2,
        `offer_chip_${i + 1}`,
        label,
        { x: b.cx, y: F.y + F.h * (up ? 0.52 : 0.56) + i * chipH * 1.3, h: chipH, align: 'center' },
        { bg: '#FFFFFF', color: P.ink, font: 'Montserrat', weight: 800 },
        {
          enter: step('slide', 1000 + i * 250, 550, 'out-quart', {
            direction: i ? 'right' : 'left',
            distance: b.s(0.4),
          }),
        },
      ),
    );
    b.addFx(s2, 'equalizer', 300, 2700, {
      count: 32,
      height: up ? 12 : 16,
      size: 84,
      y: up ? 78 : 86,
      color: P.gold,
      accent: P.accent,
    });

    // ---- Call to action: a photo, a list sliding in from the right, a pulsing button
    const s3 = b.sc('cta');
    b.backdrop(s3, 'cta_bg', lin(180, P.cream, P.peach));
    title(
      b,
      s3,
      'cta_title',
      t('Make it', 'Зроби це', 'Сделай это', 'Niech będzie', 'Fă-l', 'Hazlo'),
      t('yours', 'своїм', 'своим', 'Twoje', 'al tău', 'tuyo'),
      b.in(0, 0, 1, up ? 0.2 : 0.24),
      P.ink,
      P.accent,
      0.1,
    );
    const pw = b.pick({ upright: F.w * 0.4, square: F.w * 0.34, wide: F.w * 0.22 });
    const phh = pw * 1.4;
    const photo: Box = { x: F.x, y: F.y + F.h * (up ? 0.27 : 0.3), w: pw, h: phh };
    photoCard(s3, 'cta_photo', 'glam-portrait', photo, -3, {
      enter: step('pop', 500, 600, 'spring-bouncy'),
      emphasis: emph('float', 1200, 3000, 0),
    });
    const lx = photo.x + pw + b.s(0.04);
    const lw = F.x + F.w - lx;
    const lh = Math.min(b.s(0.085), (phh - b.s(0.02) * 3) / 4);
    [
      t(
        'Pick your size',
        'Обери розмір',
        'Выбери размер',
        'Wybierz rozmiar',
        'Alege mărimea',
        'Elige tu talla',
      ),
      t(
        'Choose a color',
        'Обери колір',
        'Выбери цвет',
        'Wybierz kolor',
        'Alege culoarea',
        'Elige el color',
      ),
      t(
        'Try it at home',
        'Приміряй удома',
        'Примерь дома',
        'Przymierz w domu',
        'Probează acasă',
        'Pruébalo en casa',
      ),
      t(
        'Fast delivery',
        'Швидка доставка',
        'Быстрая доставка',
        'Szybka dostawa',
        'Livrare rapidă',
        'Entrega rápida',
      ),
    ].forEach((label, i) =>
      row(
        b,
        s3,
        `perk_${i + 1}`,
        label,
        { x: lx, y: photo.y + i * (lh + b.s(0.02)), w: lw, h: lh },
        800 + i * 220,
        { color: '#FFFFFF', bg: P.accent },
        'right',
      ),
    );
    button(
      b,
      s3,
      t('Shop now', 'Купити', 'Купить', 'Kup teraz', 'Cumpără', 'Comprar'),
      { x: F.x, y: Math.min(photo.y + phh + b.s(0.06), F.y + F.h - b.s(0.1)), h: b.s(0.1) },
      P.ink,
      '#FFFFFF',
      1800,
    );

    b.poster = 'hook';
    return lin(160, P.bg1, P.bg2);
  },
};

export const TEMPLATES_D: VideoTemplateDef[] = [FEATURE_LAUNCH, COLLECTION_SHOWCASE];
